import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  CourseCompositeId,
  CourseStoreModel,
  getCourseCompositeId,
  Program,
  SyllabusAndCourseLevel,
  SyllabusQuery,
  SyllabusService,
  SyllabusUserSubscriptionQuery,
  SyllabusUserSubscriptionService,
  SyllabusUserSubscriptionStoreModel,
} from '@examdojo/category';
import { ErrorHandlerService } from '@examdojo/error-handling';
import { ExamDateQuery, ExamDateUIModel } from '@examdojo/exam-date';
import { UserUpdateModel } from '@examdojo/models/user';
import { mapToVoid, shareOneReplay } from '@examdojo/rxjs';
import { UserQuery, UserService } from '@examdojo/user';
import { isNotNullish } from '@examdojo/util/nullish';
import { combineLatest, EMPTY, filter, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { RootUrlParts } from '../../app.model';
import { FINAL_EXAM_ASSESSMENT_ID } from '../../features/assessments/assessment.model';
import { AssessmentService } from '../../features/assessments/assessment.service';
import { CourseFormDialogInput, CourseFormDialogService } from './course-form-dialog/course-form-dialog.service';

@Injectable({ providedIn: 'root' })
export class CourseManagementService {
  constructor(
    private readonly courseFormDialogService: CourseFormDialogService,
    private readonly userQuery: UserQuery,
    private readonly userService: UserService,
    private readonly syllabusQuery: SyllabusQuery,
    private readonly syllabusUserSubscriptionQuery: SyllabusUserSubscriptionQuery,
    private readonly syllabusUserSubscriptionService: SyllabusUserSubscriptionService,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly assessmentService: AssessmentService,
    private readonly syllabusService: SyllabusService,
    private readonly examDateQuery: ExamDateQuery,
    private readonly router: Router,
  ) {}

  readonly activeCourse$ = this.userQuery.active$.pipe(
    filter(isNotNullish),
    switchMap((user) =>
      this.syllabusQuery.courses$.pipe(
        map((courses) =>
          courses.find(
            (course) => course.syllabus_id === user.syllabus_id && course.course_level === user.course_level,
          ),
        ),
      ),
    ),
  );

  private readonly subscriptionsByCompositeId$: Observable<
    Record<CourseCompositeId, SyllabusUserSubscriptionStoreModel[]>
  > = this.syllabusUserSubscriptionQuery.entities$.pipe(
    map((subscriptions) => {
      return subscriptions.reduce<Record<CourseCompositeId, SyllabusUserSubscriptionStoreModel[]>>(
        (acc, subscription) => {
          const compositeId = getCourseCompositeId({
            syllabusId: subscription.syllabus_id,
            courseLevel: subscription.course_level,
          });
          return { ...acc, [compositeId]: [...(acc[compositeId] ?? []), subscription] };
        },
        {},
      );
    }),
    shareOneReplay(),
  );

  readonly activeProgram$ = this.userQuery.active$.pipe(map((user) => user?.program));

  readonly courses$: Observable<Array<CourseStoreModel & { isSubscribed: boolean }>> = combineLatest([
    this.syllabusQuery.courses$,
    this.activeProgram$,
    this.subscriptionsByCompositeId$,
  ]).pipe(
    map(([courses, selectedProgram, subscriptionsByCompositeId]) => {
      if (!selectedProgram) {
        return [];
      }

      return courses
        .filter((course) => course.program === selectedProgram)
        .map((course) => ({
          ...course,
          isSubscribed: !!subscriptionsByCompositeId[course.compositeId]?.length,
        }));
    }),
  );

  readonly subscribedCourses$: Observable<CourseStoreModel[]> = this.courses$.pipe(
    map((courses) => courses.filter((course) => course.isSubscribed)),
  );

  updateSelectedCourse({
    syllabusId,
    courseLevel,
    program,
  }: {
    syllabusId: UserUpdateModel['syllabus_id'];
    courseLevel: UserUpdateModel['course_level'];
    program?: UserUpdateModel['program'];
  }) {
    return this.userService
      .update(this.userQuery.getActiveId()!, {
        syllabus_id: syllabusId,
        course_level: courseLevel,
        program,
      })
      .pipe(
        switchMap(() => this.assessmentService.setActive(FINAL_EXAM_ASSESSMENT_ID)),
        tap(() => {
          if (syllabusId) {
            return this.syllabusService.setActive(syllabusId, courseLevel);
          }
        }),
      );
  }

  switchProgram(program: Program) {
    this.syllabusService.resetCourses();
    return this.syllabusService.fetchSubscribedCoursesForProgram(program).pipe(
      map((subscribedCourses) => subscribedCourses?.[0]),
      switchMap((existingSubscribedCourse) =>
        this.updateSelectedCourse({
          syllabusId: existingSubscribedCourse ? existingSubscribedCourse.syllabusId : null,
          courseLevel: existingSubscribedCourse ? existingSubscribedCourse.courseLevel : null,
          program,
        }),
      ),
      mapToVoid(),
    );
  }

  openNewCourseDialog(dialogInput?: CourseFormDialogInput) {
    return from(this.courseFormDialogService.openDialog({ input: dialogInput })).pipe(
      switchMap((result) => {
        if (!result) {
          return of(undefined);
        }

        const { course, examDateId } = result;
        const syllabusId = course.syllabus_id;
        const courseLevel = course.course_level;
        const user = this.userQuery.getActive()!;

        return this.userService.update(user.id, { syllabus_id: syllabusId, course_level: courseLevel }).pipe(
          switchMap(() =>
            this.syllabusUserSubscriptionService.upsertSubscription(user.id, syllabusId, courseLevel, examDateId),
          ),
          switchMap(() => this.updateSelectedCourse({ syllabusId, courseLevel })),
          switchMap(() => this.router.navigate([RootUrlParts.TopicPractice])),
        );
      }),
      this.errorHandlerService.catchHttpErrors(() => EMPTY),
      mapToVoid(),
    );
  }

  lookUpNewExamDate(course: SyllabusAndCourseLevel, previousExamDate: ExamDateUIModel): number | null {
    const { syllabusId, courseLevel } = course;

    const allExamDates = this.examDateQuery.getAllUIEntities();
    const futureExamDates = allExamDates.filter(this.examDateQuery.filterByFutureDatePredicate);

    const eligibleEntities = futureExamDates.filter(
      ({ syllabus_id, course_level }) => syllabus_id === syllabusId && course_level === courseLevel,
    );

    if (!eligibleEntities.length) {
      return null;
    }

    const bestMatch = eligibleEntities.reduce((closest, item) => {
      const itemDate = item.inferredDate;
      const closestDate = closest.inferredDate;
      return Math.abs(itemDate.getTime() - previousExamDate.inferredDate.getTime()) <
        Math.abs(closestDate.getTime() - previousExamDate.inferredDate.getTime())
        ? item
        : closest;
    });

    return bestMatch.id;
  }
}
